import type { SubquestionsType } from "@/_pages/ai-assistant/model";
import type { ProgramQuestionsType } from "@/_pages/ai-assistant/model/types";

export const LAST_STEP_ID = "last-step";
export const LAST_STEP_QUESTION = "Что бы вы еще хотели добавить?";
export const LAST_STEP_TYPE = "additional";

export const getOrderedProgramQuestions = (
  questions: ProgramQuestionsType[],
): ProgramQuestionsType[] => {
  return [...questions];
};

export const createInitialSubquestionData = (
  questions: ProgramQuestionsType[],
): SubquestionsType[] => {
  return [
    ...getOrderedProgramQuestions(questions).map((item) => ({
      stepId: item.documentId,
      question: item.question,
      answer: item.oneOrMany ? [] : "",
      type: item.type,
    })),
    {
      stepId: LAST_STEP_ID,
      question: LAST_STEP_QUESTION,
      answer: "",
      type: LAST_STEP_TYPE,
    },
  ];
};

export const isAnswerValid = (answer: SubquestionsType["answer"]): boolean => {
  if (Array.isArray(answer)) {
    return answer.length > 0;
  }

  return Boolean(answer?.trim());
};

export const formatQuestionAnswer = (
  answer: SubquestionsType["answer"],
): string => {
  if (Array.isArray(answer)) {
    return answer.join(", ");
  }

  return answer ?? "";
};

const getByFlexibleIndex = <T>(items: T[], index: number): T | undefined => {
  return items[index] ?? items[index - 1];
};

const getSelectedAnswerIndexes = (
  selectedAnswer: SubquestionsType["answer"],
  answers: ProgramQuestionsType["answers"],
): number[] => {
  const selectedValues = Array.isArray(selectedAnswer)
    ? selectedAnswer
    : selectedAnswer
      ? [selectedAnswer]
      : [];

  return answers.reduce<number[]>((acc, answer, answerIndex) => {
    if (selectedValues.includes(answer.answerValue)) {
      acc.push(answerIndex);
    }

    return acc;
  }, []);
};

export const isProgramQuestionVisible = (
  question: ProgramQuestionsType,
  orderedQuestions: ProgramQuestionsType[],
  subquestionData: SubquestionsType[],
): boolean => {
  const dependencyQuestion = getByFlexibleIndex(
    orderedQuestions,
    question.viewQuestion,
  );

  if (
    !dependencyQuestion ||
    dependencyQuestion.documentId === question.documentId
  ) {
    return true;
  }

  if (dependencyQuestion.answers.length === 0) {
    return true;
  }

  const selectedDependencyAnswer = subquestionData.find(
    (item) => item.stepId === dependencyQuestion.documentId,
  )?.answer;

  if (!selectedDependencyAnswer) {
    return false;
  }

  const selectedIndexes = getSelectedAnswerIndexes(
    selectedDependencyAnswer,
    dependencyQuestion.answers,
  );

  const isMatchedByIndex = selectedIndexes.some(
    (selectedIndex) =>
      selectedIndex === question.viewAnswer ||
      selectedIndex === question.viewAnswer - 1,
  );

  if (isMatchedByIndex) {
    return true;
  }

  const isMatchedByAnswerId = selectedIndexes.some(
    (selectedIndex) =>
      dependencyQuestion.answers[selectedIndex]?.id === question.viewAnswer,
  );

  return isMatchedByAnswerId;
};

export const getVisibleProgramQuestionIds = (
  orderedQuestions: ProgramQuestionsType[],
  subquestionData: SubquestionsType[],
): string[] => {
  return orderedQuestions
    .filter((question) =>
      isProgramQuestionVisible(question, orderedQuestions, subquestionData),
    )
    .map((question) => question.documentId);
};
